import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  ReactNode,
  useEffect,
} from 'react';
import { Select, Tag, Tooltip, SelectProps } from 'antd';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface SortableTagProps {
  id: string;
  label: ReactNode;
  onClose: () => void;
  style?: React.CSSProperties;
}

const SortableTag: React.FC<SortableTagProps> = ({ id, label, onClose, ...props }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: 'grab',
    margin: '2px',
    display: 'inline-flex',
    alignItems: 'center',
    zIndex: isDragging ? 1000 : 'auto',
    boxShadow: isDragging ? '0 3px 6px rgba(0,0,0,0.2)' : 'none',
    ...props.style,
  };

  return (
    <Tag
      ref={setNodeRef}
      style={style}
      closable
      onClose={onClose}
      onMouseDown={(e) => e.stopPropagation()}
      {...attributes}
      {...listeners}
      color="blue"
    >
      {label}
    </Tag>
  );
};

interface SortableMultiSelectProps extends SelectProps<string[]> {
  options: { label: string; value: string }[];
  value?: string[];
  onChange?: (value: string[]) => void;
}

const SortableMultiSelect: React.FC<SortableMultiSelectProps> = ({
  options,
  value,
  onChange,
  ...restProps
}) => {
  const [internalValue, setInternalValue] = useState<string[]>(value || []);
  const selectRef = useRef<any>(null);

  useEffect(() => {
    setInternalValue(value || []);
  }, [value]);

  const activeIds = useMemo(() => internalValue, [internalValue]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (active.id !== over?.id) {
        setInternalValue((items) => {
          const oldIndex = items.indexOf(active.id as string);
          const newIndex = items.indexOf(over?.id as string);
          const newOrder = [...items];
          const [movedItem] = newOrder.splice(oldIndex, 1);
          newOrder.splice(newIndex, 0, movedItem);

          onChange?.(newOrder);
          return newOrder;
        });
      }
    },
    [onChange]
  );

  const tagRender = useCallback(
    (props: {
      label: ReactNode;
      value: string;
      onClose: () => void;
    }) => {
      const { label, value: tagValue, onClose } = props;

      const handleTagClose = () => {
        const newValue = internalValue.filter((item) => item !== tagValue);
        setInternalValue(newValue);
        onChange?.(newValue);
        onClose();
      };

      if (!internalValue.includes(tagValue)) {
        return <></>; // return empty fragment instead of null
      }

      return (
        <SortableTag
          id={tagValue}
          label={label}
          onClose={handleTagClose}
        />
      );
    },
    [internalValue, onChange]
  );

  const handleSelectChange = useCallback(
    (newValue: string[]) => {
      const existingItems = internalValue.filter((item) => newValue.includes(item));
      const newItems = newValue.filter((item) => !internalValue.includes(item));
      const updatedOrder = [...existingItems, ...newItems];

      setInternalValue(updatedOrder);
      onChange?.(updatedOrder);

      selectRef.current?.focus();
    },
    [internalValue, onChange]
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={activeIds} strategy={horizontalListSortingStrategy}>
        <Tooltip title="Drag and drop tag to reorder your selection.">
          <Select
            ref={selectRef}
            mode="multiple"
            value={internalValue}
            onChange={handleSelectChange}
            tagRender={tagRender}
            options={options}
            style={{ paddingTop: '2px', paddingBottom: '2px' }}
            {...restProps}
          />
        </Tooltip>
      </SortableContext>
    </DndContext>
  );
};

export default SortableMultiSelect;